import { DefaultParamType, TFnType } from '@tolgee/react'
import { round } from 'lodash'

import {
  CategoryRevenuePercentageFragment,
  ProductDashboardIntervalStatFragment,
  ProductDashboardStatFragment,
} from '@/graphql'
import { ProductDashboardStat } from '@/mocks/mockSummaryStats'
import { DAIOverTimeLineChartSeries } from '@/pages/charts/ApexCharts/DAIOverTimeLineChart'

const getProductStatColumns = (t: (key: string) => string) => {
  return {
    TOTAL_REVENUE: t('statColumns.totalRevenue'),
    VOLUME: t('statColumns.volume'),
    VPO: t('statColumns.vpo'),
    EMPTY_FACINGS: t('statColumns.emptyFacings'),
    FACINGS: t('statColumns.facings'),
    SOVI: t('statColumns.sovi'),
    REVENUE_PER_FACING: t('statColumns.revenuePerFacing'),
    EARNINGS_PER_FACING: t('statColumns.marginPerFacing'),
    TOTAL_EARNINGS: t('statColumns.totalMargin'),
  }
}

const transformStats = (
  data: ProductDashboardStatFragment | undefined,
  t: (key: string) => string,
): ProductDashboardStat[] => {
  const statColumns = getProductStatColumns(t)

  return [
    {
      label: statColumns.TOTAL_REVENUE,
      valueType: 'CURRENCY',
      value: data?.totalRevenue,
      name: 'rev',
    },
    {
      label: statColumns.TOTAL_EARNINGS,
      valueType: 'CURRENCY',
      value: data?.totalEarnings,
      name: 'margin',
    },
    {
      label: statColumns.VOLUME,
      valueType: 'COMBINED_VOLUME_VPO',
      value: data?.volume,
      perValue: data?.vpo,
      name: 'vol',
    },
    {
      label: statColumns.FACINGS,
      valueType: 'SINGLE_VALUE',
      value: data?.facings,
      name: 'facings',
    },
    {
      label: statColumns.EMPTY_FACINGS,
      valueType: 'SINGLE_VALUE',
      value: data?.emptyFacings,
      name: 'empty',
    },
    {
      label: statColumns.REVENUE_PER_FACING,
      valueType: 'CURRENCY',
      value: data?.revenuePerFacing,
      name: 'rpf',
    },
    {
      label: statColumns.EARNINGS_PER_FACING,
      valueType: 'CURRENCY',
      value: data?.earningsPerFacing,
      name: 'epf',
    },
  ]
}

const transformProductCategoryRevenuePercentage = (
  data: CategoryRevenuePercentageFragment | null | undefined,
  t: (key: string) => string,
): ProductDashboardStat => ({
  label: t('statColumns.categoryRevenuePercentage'),
  valueType: 'PERCENTAGE',
  value: data?.currentPercentage,
  previousValue: data?.previousPercentage,
  name: 'pctCatRev',
})

const addSeries = (
  t: TFnType<DefaultParamType, string, any>,
  data: ProductDashboardIntervalStatFragment[],
  series: DAIOverTimeLineChartSeries[],
  statKey: keyof ProductDashboardIntervalStatFragment,
  convertToPercentage: boolean,
  dataType: 'number' | 'currency' | 'percentage',
) => {
  const values = data.map(item => item[statKey])

  if (values.some(val => val !== null)) {
    series.push({
      name: statKey,
      id: statKey,
      data: values.map(val =>
        val === null
          ? null
          : round(
              parseFloat((val as number).toFixed(2)) *
                (convertToPercentage ? 100 : 1),
            ),
      ),
      dataType,
    })
  }
}

const transformIntervalDataForCharts = (
  t: TFnType<DefaultParamType, string, any>,
  data: ProductDashboardIntervalStatFragment[] | undefined,
): DAIOverTimeLineChartSeries[] => {
  if (!data || data.length === 0) {
    return []
  }
  const series: DAIOverTimeLineChartSeries[] = []
  addSeries(t, data, series, 'totalRevenue', false, 'currency')
  addSeries(t, data, series, 'totalEarnings', false, 'currency')
  addSeries(t, data, series, 'volume', false, 'number')
  addSeries(t, data, series, 'vpo', false, 'number')
  addSeries(t, data, series, 'facings', false, 'number')
  addSeries(t, data, series, 'emptyFacings', false, 'number')
  addSeries(t, data, series, 'sovi', true, 'percentage')
  addSeries(t, data, series, 'earningsPerFacing', false, 'currency')

  return series
}

export const ProductDashboardStatsHelpers = {
  transformStats,
  transformIntervalDataForCharts,
  transformProductCategoryRevenuePercentage,
}
